import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Menu } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useNotifications } from '../hooks/useNotifications';
import { mobileTabs } from '../config/modules';
import MobileMenu from './MobileMenu';
import styles from './BottomNav.module.css';

/**
 * Primary navigation on a phone.
 *
 * Four destinations plus the menu. It used to carry five, two of which — Home
 * and Dashboard — pointed at the same page once the two dashboards were merged,
 * so one of the five slots was doing nothing. That slot is now the way into
 * everything else: below 1024px the sidebar is hidden, and without this there
 * was no route to most of the app at all.
 *
 * `currentPage` is the same id the sidebar uses.
 */
function BottomNav({ currentPage }) {
  const { user } = useAuth();
  const { unreadCount } = useNotifications();
  const [menuOpen, setMenuOpen] = useState(false);
  const tabs = mobileTabs(user);

  if (!tabs) return null;

  return (
    <>
      <nav className={styles.bar} aria-label="Main">
        {tabs.map((tab) => {
          const active = currentPage === tab.id;
          const Icon = tab.icon;
          // Messages is the only tab with something to announce; the bell in the
          // top bar covers everything else, so one badge is enough.
          const badge = tab.id === 'messages' ? unreadCount : 0;
          return (
            <Link
              key={tab.id}
              to={tab.path}
              className={[styles.tab, active ? styles.active : ''].filter(Boolean).join(' ')}
              aria-current={active ? 'page' : undefined}
            >
              <span className={styles.iconWrap}>
                <Icon size={23} strokeWidth={active ? 2.3 : 1.9} aria-hidden="true" />
                {badge > 0 && <span className={styles.badge}>{badge > 9 ? '9+' : badge}</span>}
              </span>
              <span className={styles.label}>{tab.label}</span>
            </Link>
          );
        })}

        <button
          type="button"
          className={[styles.tab, menuOpen ? styles.active : ''].filter(Boolean).join(' ')}
          onClick={() => setMenuOpen(true)}
          aria-haspopup="dialog"
          aria-expanded={menuOpen}
        >
          <span className={styles.iconWrap}>
            <Menu size={23} strokeWidth={menuOpen ? 2.3 : 1.9} aria-hidden="true" />
          </span>
          <span className={styles.label}>Menu</span>
        </button>
      </nav>

      <MobileMenu open={menuOpen} onClose={() => setMenuOpen(false)} />
    </>
  );
}

export default BottomNav;
